import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button,
  CircularProgress, Typography, TextField, Box, Container, Pagination, Card, CardContent,
  Avatar, FormGroup, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, InputAdornment, IconButton, Menu
} from '@mui/material';
import {
  SaveAlt as SaveAltIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { API_BASE_URL } from '../constants/APIConstants';
import * as XLSX from 'xlsx';
import { auth } from '../firebase';
import { formatDate } from './WaitlistDashboard';
import AccountDetails from './AccountDetails';
import './adminDashboard.css';

const convertToTitleCase = (camelCaseString) => {
  if (!camelCaseString || typeof camelCaseString !== 'string') return '';
  return camelCaseString
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const BrandAmbassadorDashboard = () => {
  const [currentTab, setCurrentTab] = useState('pending');
  const [tableData, setTableData] = useState([]);
  const [tabCounts, setTabCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    portfolioImages: null,
    isEmailVerified: null,
    hasDescription: null,
    socialMediaLinked: null,
  });

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

  useEffect(() => {
    const loadCountsFromLocalStorage = () => {
      const pendingCount = localStorage.getItem('ba_pendingCount');
      const approvedCount = localStorage.getItem('ba_approvedCount');
      const rejectedCount = localStorage.getItem('ba_rejectedCount');
      setTabCounts({
        pending: pendingCount ? parseInt(pendingCount) : 0,
        approved: approvedCount ? parseInt(approvedCount) : 0,
        rejected: rejectedCount ? parseInt(rejectedCount) : 0,
      });
    };
    loadCountsFromLocalStorage();
    fetchData(currentTab);
  }, [currentTab]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value === prev[filterName] ? null : value,
    }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      portfolioImages: null,
      isEmailVerified: null,
      hasDescription: null,
      socialMediaLinked: null,
    });
    setCurrentPage(1);
  };

  const closeFilterMenu = () => {
    setFilterAnchorEl(null);
  };

  const handleViewProfile = (accountId) => {
    const account = tableData.find((account) => account.accountId === accountId);
    if (account) {
      setSelectedAccount(account);
      setIsAccountModalOpen(true);
    }
  };

  const handleBrandAmbassadorStatusUpdate = async (accountId, status) => {
    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();
      await axios.put(
        `${API_BASE_URL}/api/admin/updateBrandAmbassadorStatus`,
        null,
        {
          params: { accountId, status },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchData(currentTab);
    } catch (error) {
      console.error('Error updating brand ambassador status:', error);
      alert('Failed to update brand ambassador status.');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (status) => {
    setLoading(true);

    const apiUrl = `${API_BASE_URL}/api/data/getAllBrandAmbassadorUsingStatus?status=${status}`;
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await axios.get(apiUrl, { headers: { Authorization: `Bearer ${token}` } });

      if (!response.data || response.data.length === 0) {
        setTableData([]);
        setTabCounts((prevCounts) => {
          localStorage.setItem(`ba_${status}Count`, 0);
          return { ...prevCounts, [status]: 0 };
        });
        return;
      }

      const transformedData = response.data.map((item) => ({
        accountId: item.accountDetails?.accountId || 'N/A',
        name: item.accountDetails?.accountName || 'N/A',
        email: item.accountDetails?.accountEmail || 'N/A',
        contact: item.accountDetails?.accountPhone || 'N/A',
        portfolioImages: item.portfolioCollections?.some(
          (collection) => Array.isArray(collection.portfolioDocuments) && collection.portfolioDocuments.length > 0
        ) ? 'Yes' : 'No',
        isEmailVerified: item.emailVerified,
        accountDescription: item.accountDetails?.accountDescription || 'N/A',
        socialMediaLinked: item.accountDetails?.socialMediaLinked,
        socialLinks: item.accountDetails?.socialLinks || item.accountDetails?.socials || item.accountDetails?.socialMediaLinks || {},
        accountCreatedAt: formatDate(item.accountDetails?.accountCreatedAt),
        accountProfileURL: item.accountDetails?.accountProfileURL || '',
        accountCategory: convertToTitleCase(item.accountDetails?.accountCategory) || 'No',
        accountSubCategory: convertToTitleCase(item.accountDetails?.accountSubCategory) || 'No',
        status: item.status || status,
        accountRating: item.accountDetails?.accountRating || '0',
        accountTotalRatings: item.accountDetails?.accountTotalRatings || '0',
        referralCode: item.referralCode || 'N/A',
        brandAmbassadorAppliedAt: formatDate(item.brandAmbassadorAppliedAt),
        brandAmbassadorApprovedAt: formatDate(item.brandAmbassadorApprovedAt),
        portfolioCollections: item.portfolioCollections || [],
        Profile: item.accountDetails?.accountProfileURL || 'N/A',
      }));

      setTableData(transformedData);
      setTabCounts((prevCounts) => {
        const updatedCounts = { ...prevCounts, [status]: transformedData.length };
        localStorage.setItem(`ba_${status}Count`, transformedData.length);
        return updatedCounts;
      });
    } catch (error) {
      const statusCode = error?.response?.status;
      const body = error?.response?.data;
      console.error('Error fetching data:', { url: apiUrl, statusCode, body, error });
      if (statusCode === 401 || statusCode === 403) {
        alert(`Access error ${statusCode}. Make sure you are logged into the correct Firebase project and have admin rights in PROD.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredData = tableData.filter((row) => {
    const searchTerm = searchText.toLowerCase();
    const matchesSearch = (
      (row.name && row.name.toLowerCase().includes(searchTerm)) ||
      (row.email && row.email.toLowerCase().includes(searchTerm)) ||
      (row.referralCode && row.referralCode.toLowerCase().includes(searchTerm))
    );
    const matchesFilters = (
      (filters.portfolioImages === null || row.portfolioImages === (filters.portfolioImages ? 'Yes' : 'No')) &&
      (filters.isEmailVerified === null || row.isEmailVerified === filters.isEmailVerified) &&
      (filters.hasDescription === null || (row.accountDescription !== 'N/A') === filters.hasDescription) &&
      (filters.socialMediaLinked === null || row.socialMediaLinked === filters.socialMediaLinked)
    );
    return matchesSearch && matchesFilters;
  });

  const exportToExcel = () => {
    // Export what's currently visible (search + filters applied), not the
    // full unfiltered tab dataset.
    const rowsToExport = filteredData.map(({ accountId, accountProfileURL, portfolioCollections, Profile, ...rest }) => rest);
    const headers = Object.keys(rowsToExport[0] || {}).map(convertToTitleCase);
    const dataWithHeaders = [headers, ...rowsToExport.map(Object.values)];
    const worksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BrandAmbassadors');
    XLSX.writeFile(workbook, 'BrandAmbassadorData.xlsx');
  };

  const handleRefresh = () => {
    fetchData(currentTab);
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const currentPageData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const statusMapping = {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
  };

  const statusDescriptions = {
    pending: 'Awaiting review',
    approved: 'Accepted partners',
    rejected: 'Declined requests',
  };

  const activeFilterCount = Object.values(filters).filter((value) => value !== null).length;
  const isFilterMenuOpen = Boolean(filterAnchorEl);

  const tableHeaders = [
    'Created At',
    'Account',
    'Referral Code',
    'Verification',
    'Timeline',
    'Actions',
  ];

  return (
    <Container maxWidth={false} className="dashboard-page">
      <Box className="dashboard-header">
        <Box>
          <div className="dashboard-eyebrow">Partner Applications</div>
          <h1 className="dashboard-title">Brand Ambassador Dashboard</h1>
          <div className="dashboard-subtitle">
            Review ambassador applications, portfolio readiness, and referral details.
          </div>
        </Box>
        <Box className="dashboard-actions">
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<SaveAltIcon />} onClick={exportToExcel}>
            Export
          </Button>
        </Box>
      </Box>

      <Box className="dashboard-metrics">
        {Object.keys(statusMapping).map((status) => (
          <Card
            key={status}
            className={`metric-card metric-card--${status} ${currentTab === status ? 'metric-card--active' : ''}`}
            onClick={() => setCurrentTab(status)}
          >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <div className="metric-card__label">{statusMapping[status]}</div>
              <div className="metric-card__value">{tabCounts[status] || 0}</div>
              <div className="table-muted-text">{statusDescriptions[status]}</div>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box className="dashboard-toolbar">
        <TextField
          className="dashboard-search"
          variant="outlined"
          placeholder="Search name, email, or referral code"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText('')} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Chip label={`${filteredData.length} visible`} variant="outlined" />
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={(event) => setFilterAnchorEl(event.currentTarget)}
        >
          Filters
          {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </Button>
        <Menu
          anchorEl={filterAnchorEl}
          open={isFilterMenuOpen}
          onClose={closeFilterMenu}
          PaperProps={{ className: 'filter-menu' }}
        >
          <Box className="filter-menu__content">
            <Box className="filter-panel__header">
              <div className="filter-panel__title">Filters</div>
              <Chip size="small" label={`${activeFilterCount} active`} color={activeFilterCount ? 'primary' : 'default'} />
            </Box>
            <FormGroup className="filter-panel__controls">
              <FormControlLabel
                control={<Checkbox checked={filters.portfolioImages === true} onChange={(e) => handleFilterChange('portfolioImages', e.target.checked)} />}
                label="Has portfolio"
              />
              <FormControlLabel
                control={<Checkbox checked={filters.isEmailVerified === true} onChange={(e) => handleFilterChange('isEmailVerified', e.target.checked)} />}
                label="Email verified"
              />
              <FormControlLabel
                control={<Checkbox checked={filters.hasDescription === true} onChange={(e) => handleFilterChange('hasDescription', e.target.checked)} />}
                label="Has description"
              />
              <FormControlLabel
                control={<Checkbox checked={filters.socialMediaLinked === true} onChange={(e) => handleFilterChange('socialMediaLinked', e.target.checked)} />}
                label="Social linked"
              />
            </FormGroup>
            <Box className="filter-menu__footer">
              <Button size="small" onClick={resetFilters} disabled={!activeFilterCount}>
                Reset filters
              </Button>
              <Button size="small" variant="contained" onClick={closeFilterMenu}>
                Done
              </Button>
            </Box>
          </Box>
        </Menu>
      </Box>

      <TableContainer component={Paper} className="admin-table-card">
        <Table size="small" stickyHeader className="admin-table">
          <TableHead>
            <TableRow>
              {tableHeaders.map((header, index) => (
                <TableCell key={index}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tableHeaders.length} align="center" className="table-empty-state">
                  <CircularProgress />
                  <Typography variant="body2" mt={1}>Loading applications...</Typography>
                </TableCell>
              </TableRow>
            ) : currentPageData.length > 0 ? (
              currentPageData.map((row) => (
                <TableRow
                  key={row.accountId}
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleViewProfile(row.accountId)}
                >
                  <TableCell>{row.accountCreatedAt}</TableCell>
                  <TableCell>
                    <Box className="table-account-cell">
                      <Avatar
                        src={row.accountProfileURL}
                        alt="Profile"
                        sx={{ width: 38, height: 38 }}
                      />
                      <Box>
                        <div className="table-primary-text">{row.name}</div>
                        <div className="table-muted-text">{row.email}</div>
                        <div className="table-muted-text">{row.contact}</div>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><Chip size="small" label={row.referralCode} variant="outlined" /></TableCell>
                  <TableCell>
                    <Box className="table-chip-group">
                      <Chip size="small" label={`Portfolio: ${row.portfolioImages}`} color={row.portfolioImages === 'Yes' ? 'success' : 'default'} variant="outlined" />
                      <Chip size="small" label={`Email: ${row.email && row.email !== 'N/A' ? 'Yes' : 'No'}`} color={row.email && row.email !== 'N/A' ? 'success' : 'default'} variant="outlined" />
                      <Chip size="small" label={`Social: ${row.socialMediaLinked ? 'Yes' : 'No'}`} color={row.socialMediaLinked ? 'success' : 'default'} variant="outlined" />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <div className="table-primary-text">Joined: {row.brandAmbassadorAppliedAt}</div>
                    <div className="table-muted-text">Approved: {row.brandAmbassadorApprovedAt}</div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box className="table-actions">
                    {currentTab === 'pending' && (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleBrandAmbassadorStatusUpdate(row.accountId, 'approved')}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleBrandAmbassadorStatusUpdate(row.accountId, 'rejected')}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewProfile(row.accountId)}
                    >
                      View
                    </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeaders.length} align="center" className="table-empty-state">
                  <Typography variant="subtitle1" fontWeight={800}>No records found</Typography>
                  <Typography variant="body2">Try a different status tab, search term, or filter combination.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className="dashboard-pagination">
        <Typography variant="body2" color="text.secondary">
          Showing {currentPageData.length} of {filteredData.length} records
        </Typography>
        <Pagination count={totalPages} page={currentPage} onChange={(_, page) => setCurrentPage(page)} color="primary" />
      </Box>

      {/* Dialog for Account Details */}
      <Dialog
        open={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ className: 'profile-dialog' }}
      >
        <DialogTitle className="profile-dialog__title">
          <Box>
            <Typography className="profile-dialog__eyebrow">Admin Review</Typography>
            <Typography className="profile-dialog__heading">Account Profile</Typography>
          </Box>
          <Button onClick={() => setIsAccountModalOpen(false)} className="profile-dialog__close">
            Close
          </Button>
        </DialogTitle>
        <DialogContent className="profile-dialog__content">
          {selectedAccount ? (
            <AccountDetails
              account={selectedAccount}
              onClose={() => setIsAccountModalOpen(false)}
              onStatusChange={() => fetchData(currentTab)}
            />
          ) : (
            <Typography>No account data available.</Typography>
          )}
        </DialogContent>
        <DialogActions className="profile-dialog__footer">
          <Typography variant="caption" color="text.secondary">
            Review profile details before changing account status.
          </Typography>
          <Button onClick={() => setIsAccountModalOpen(false)} color="primary" variant="outlined">Done</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BrandAmbassadorDashboard;
